import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ShowcasesApiService } from '@birco/rainmesh-api';
import { UserService } from '@sso/ld-sso-with-oauth2-oidc';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { DemoLoginTokenService } from './demo-login-token.service';
import { RouteConstants } from '../../shared/constants/route-constants';
import { Role } from '../enums/role.enum';

/**
 * `renewed`: the lease is confirmed active, `leasedUntil` is the new source of truth.
 * `expired`: the backend rejected the call with 401 - the usage is gone server-side.
 * `unknown`: some other error (e.g. network) - the lease's actual state is unconfirmed.
 */
export type ShowcaseLeaseResult =
  | { status: 'renewed'; leasedUntil: Date }
  | { status: 'expired' }
  | { status: 'unknown' };

export type ShowcaseGroupInvitationResult =
  | { status: 'created'; link: string }
  | { status: 'error' };

@Injectable({
  providedIn: 'root',
})
export class ShowcaseSessionService {
  private readonly apiService = inject(ShowcasesApiService);
  private readonly demoLoginTokenService = inject(DemoLoginTokenService);
  private readonly userService = inject(UserService<Role>);

  startSession(showcaseId: string): void {
    this.demoLoginTokenService.persistShowcaseId(showcaseId);
  }

  /**
   * Renews the lease of the showcase usage held by the caller.
   * Backend-enforced for the holder (RM_DEMO) only - a group member
   * (RM_DEMO_GROUP) would be rejected with 401, so it's a no-op here instead.
   */
  renewLease(): Observable<ShowcaseLeaseResult> {
    const showcaseId = this.demoLoginTokenService.getShowcaseId();
    if (!showcaseId) {
      return of({ status: 'expired' as const });
    }

    return this.userService.hasRole(Role.RM_DEMO).pipe(
      switchMap((isHolder) => {
        if (!isHolder) {
          return of({ status: 'unknown' as const });
        }

        return this.apiService.renewShowcaseLease(showcaseId).pipe(
          map(
            (response) =>
              ({
                status: 'renewed' as const,
                leasedUntil: new Date(response.leasedUntil),
              }) satisfies ShowcaseLeaseResult,
          ),
          catchError((error: unknown) => of(this.toLeaseResult(error))),
        );
      }),
    );
  }

  /** Fetches the caller's currently held showcase, to (re)sync the lease after login or a reload. */
  fetchCurrentLease(): Observable<ShowcaseLeaseResult> {
    return this.apiService.getMyShowcase().pipe(
      map((response) => {
        this.demoLoginTokenService.persistShowcaseId(response.showcaseId);
        return {
          status: 'renewed' as const,
          leasedUntil: new Date(response.leasedUntil),
        } satisfies ShowcaseLeaseResult;
      }),
      catchError((error: unknown) => of(this.toLeaseResult(error))),
    );
  }

  private toLeaseResult(error: unknown): ShowcaseLeaseResult {
    return error instanceof HttpErrorResponse && error.status === 401
      ? { status: 'expired' }
      : { status: 'unknown' };
  }

  /** Generates (or, once already generated for this usage, re-fetches) the invite link for companions. */
  generateGroupInvitation(): Observable<ShowcaseGroupInvitationResult> {
    const showcaseId = this.demoLoginTokenService.getShowcaseId();
    if (!showcaseId) {
      return of({ status: 'error' as const });
    }

    return this.apiService.generateShowcaseGroupInvitation(showcaseId).pipe(
      map(
        (response) =>
          ({
            status: 'created' as const,
            link: this.buildInvitationLink(showcaseId, response.groupId),
          }) satisfies ShowcaseGroupInvitationResult,
      ),
      catchError(() => of({ status: 'error' as const })),
    );
  }

  private buildInvitationLink(scenarioId: string, groupId: string): string {
    const path = RouteConstants.routeDemoLogin.replace(
      ':scenarioId',
      scenarioId,
    );
    return `${window.location.origin}/${path}?groupId=${encodeURIComponent(groupId)}`;
  }

  /**
   * Frees the currently held showcase, if any, before the caller proceeds to log out.
   * Backend-enforced for the holder (RM_DEMO) only - a group member
   * (RM_DEMO_GROUP) would be rejected with 401, so it's a no-op here instead.
   */
  endSession(): Observable<void> {
    const showcaseId = this.demoLoginTokenService.getShowcaseId();
    if (!showcaseId) {
      return of(undefined);
    }

    return this.userService.hasRole(Role.RM_DEMO).pipe(
      switchMap((isHolder) => {
        if (!isHolder) {
          this.demoLoginTokenService.clearShowcaseId();
          return of(undefined);
        }

        return this.apiService.freeShowcase(showcaseId).pipe(
          catchError(() => of(undefined)),
          map(() => {
            this.demoLoginTokenService.clearShowcaseId();
          }),
        );
      }),
    );
  }

  /**
   * Checks whether a group member's session is still valid, via the same
   * "whoami" call the holder uses to (re)sync their lease (fetchCurrentLease).
   * Once the holder ends the usage or its lease expires, the group member's
   * access token no longer maps to anything valid and this starts failing
   * with 401.
   */
  checkGroupSessionAlive(): Observable<boolean> {
    return this.apiService.getMyShowcase().pipe(
      map(() => true),
      catchError((error: unknown) =>
        of(!(error instanceof HttpErrorResponse && error.status === 401)),
      ),
    );
  }
}
